/**
 * Styled Components Mixins
 * Reusable style patterns and utilities
 */

import { css } from 'styled-components';

/**
 * Flexbox utilities
 */
export const flexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const flexColumn = css`
  display: flex;
  flex-direction: column;
`;

export const flexRow = css`
  display: flex;
  flex-direction: row;
`;

export const flexBetween = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

/**
 * Text utilities
 */
export const textEllipsis = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const textCenter = css`
  text-align: center;
`;

/**
 * Scrollbar styling
 */
export const hideScrollbar = css`
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const customScrollbar = css`
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.grayLighter};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.gray};
    border-radius: ${({ theme }) => theme.borderRadius.sm};

    &:hover {
      background: ${({ theme }) => theme.colors.grayDark};
    }
  }
`;

/**
 * Focus state
 */
export const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

/**
 * Disabled state
 */
export const disabledState = css`
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/**
 * Hover effects
 */
export const hoverScale = css`
  transition: transform ${({ theme }) => theme.transitions.fast};
  
  &:hover:not(:disabled) {
    transform: scale(1.02);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

export const hoverOpacity = css`
  transition: opacity ${({ theme }) => theme.transitions.fast};
  
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

/**
 * Card styles - Premium aligned
 * Premium specs: border-radius 0.75rem (12px), border 1px solid rgb(216, 221, 236)
 */
export const cardBase = css`
  background-color: ${({ theme }) => theme.colors.bgPrimary}; /* Uses theme-appropriate background */
  border-radius: ${({ theme }) => theme.borderRadius.lg}; /* 12px / 0.75rem - Premium spec */
  border: 1px solid ${({ theme }) => theme.colors.grayLight}; /* rgb(216, 221, 236) - Premium spec */
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

export const cardHover = css`
  ${cardBase}
  transition: box-shadow ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

/**
 * Button base styles - Premium aligned
 * Premium specs: font-size 0.75rem (12px), font-weight 400, border-radius 16px, padding 0.75rem
 */
export const buttonBase = css`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 16px; /* Premium spec */
  outline: none;
  font-family: ${({ theme }) => theme.typography.fontFamily}; /* Poppins */
  font-size: 0.75rem; /* 12px - Premium spec */
  font-weight: 400; /* normal - Premium spec */
  text-transform: capitalize;
  cursor: pointer;
  transition: 0.25s ease-in-out; /* Premium spec */
  ${focusRing}
  ${disabledState}
  
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

/**
 * Input base styles - Premium aligned
 * Premium specs: padding 0.75rem (12px), font-size 0.75rem (12px), height 27px, border-radius 0.25rem (4px)
 */
export const inputBase = css`
  width: 100%;
  padding: 0.75rem; /* 12px - Premium spec */
  height: 27px; /* Premium spec */
  border: none; /* Border is on container */
  border-radius: 0.25rem; /* 4px - Premium spec */
  font-family: ${({ theme }) => theme.typography.fontFamily}; /* Poppins */
  font-size: 0.75rem; /* 12px - Premium spec */
  font-weight: 400; /* Premium spec */
  color: rgb(125, 125, 125); /* Premium spec */
  background-color: rgb(255, 255, 255); /* Premium spec */
  outline: none;
  box-shadow: none;
  transition: all ${({ theme }) => theme.transitions.fast};
  -webkit-tap-highlight-color: transparent;
  
  &:focus {
    outline: none;
  }
  
  ${disabledState}
  
  &::placeholder {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

/**
 * Status badge styles
 */
export const statusBadge = (status: 'success' | 'error' | 'warning' | 'info') => css`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  
  ${({ theme }) => {
    switch (status) {
      case 'success':
        return css`
          background-color: ${theme.colors.successLight};
          color: ${theme.colors.successDark};
          border: 1px solid ${theme.colors.success};
        `;
      case 'error':
        return css`
          background-color: ${theme.colors.errorLight};
          color: ${theme.colors.errorDark};
          border: 1px solid ${theme.colors.error};
        `;
      case 'warning':
        return css`
          background-color: ${theme.colors.warningLight};
          color: ${theme.colors.warningDark};
          border: 1px solid ${theme.colors.warning};
        `;
      case 'info':
        return css`
          background-color: ${theme.colors.infoLight};
          color: ${theme.colors.infoDark};
          border: 1px solid ${theme.colors.info};
        `;
    }
  }}
`;

/**
 * Fade in animation
 */
export const fadeIn = css`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  animation: fadeIn ${({ theme }) => theme.transitions.base};
`;

/**
 * Slide in animation
 */
export const slideIn = (direction: 'up' | 'down' | 'left' | 'right' = 'up') => css`
  @keyframes slideIn {
    from {
      transform: ${() => {
        switch (direction) {
          case 'up': return 'translateY(20px)';
          case 'down': return 'translateY(-20px)';
          case 'left': return 'translateX(20px)';
          case 'right': return 'translateX(-20px)';
        }
      }};
      opacity: 0;
    }
    to {
      transform: translate(0);
      opacity: 1;
    }
  }
  
  animation: slideIn ${({ theme }) => theme.transitions.base};
`;

/**
 * Responsive utilities
 */
export const mobile = (content: ReturnType<typeof css>) => css`
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    ${content}
  }
`;

export const tablet = (content: ReturnType<typeof css>) => css`
  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) and (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    ${content}
  }
`;

export const desktop = (content: ReturnType<typeof css>) => css`
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    ${content}
  }
`;

/**
 * Truncate text with line clamp
 */
export const lineClamp = (lines: number) => css`
  display: -webkit-box;
  -webkit-line-clamp: ${lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/**
 * Visually hidden (for accessibility)
 */
export const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
